import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useDropzone } from "react-dropzone";
import moment from "moment";
import { toastAlert } from "@paylixecommerce/shared/components";
import { BackIcon } from "./ThemeSettings";
import { useThemesContext } from "../../../context/ThemeProvider";
import { Modal, Button } from "components";
import ImageGalleryItem from "./ImageGalleryItem";


const ImageIcon = () => <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M15 8.5H15.01" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  <path d="M17 4.5H7C5.34315 4.5 4 5.84315 4 7.5V17.5C4 19.1569 5.34315 20.5 7 20.5H17C18.6569 20.5 20 19.1569 20 17.5V7.5C20 5.84315 18.6569 4.5 17 4.5Z" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  <path d="M4 15.498L8 11.498C8.45606 11.0592 8.97339 10.8281 9.5 10.8281C10.0266 10.8281 10.5439 11.0592 11 11.498L16 16.498" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  <path d="M14 14.498L15 13.498C15.4561 13.0592 15.9734 12.8281 16.5 12.8281C17.0266 12.8281 17.5439 13.0592 18 13.498L20 15.498" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>

const QuestionIcon = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  <path d="M8 11.3359V11.3426" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  <path d="M7.99935 9.00076C7.98707 8.78434 8.04545 8.5698 8.16569 8.38944C8.28593 8.20908 8.46152 8.07267 8.66602 8.00076C8.9166 7.90494 9.14152 7.75225 9.32306 7.55473C9.50461 7.35721 9.63783 7.12024 9.71223 6.86249C9.78663 6.60473 9.80019 6.33322 9.75183 6.06934C9.70348 5.80545 9.59453 5.55639 9.43356 5.34177C9.27259 5.12715 9.064 4.95282 8.82421 4.83251C8.58442 4.7122 8.31998 4.64919 8.0517 4.64844C7.78342 4.6477 7.51863 4.70924 7.27818 4.82822C7.03773 4.9472 6.82817 5.12037 6.66602 5.33409" stroke="#555D67" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
</svg>


function parseSize(size) {
  if (!size) return '';
  if (size > 1000) {
    const sizeKB = Math.round(size / 1024);
    if (sizeKB > 1000) {
      const sizeMB = Math.round(sizeKB / 1024);
      if (sizeMB > 1000) {
        return `${Math.round(sizeMB / 1024)} GB`;
      }
      return `${sizeMB} MB`;
    }
    return `${sizeKB} KB`;
  }
  return `${size} B`;
}

function ImageGallery({ onBack }) {
  const { themeId } = useParams();
  const { uploadImages, selectedTheme, deleteThemeAsset } = useThemesContext();

  const [allImages, setAllImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [confirm, showConfirm] = useState(false);

  const handleUploadImages = event => {
    event.stopPropagation();

    open();
  };

  const onDrop = async (acceptedImages, fileRejections) => {
    if (fileRejections.length) {
      const errorMessage = (
        <div>
          <ul>
            {fileRejections.map((errorInfo) => {
              return (
                <li style={{paddingBottom: '1rem'}}>
                  {errorInfo.file.name} ({errorInfo.errors.map((error) => error.message).join('; ')})
                </li>
              );
            })}
          </ul>
        </div>
      )
      toastAlert('error', errorMessage);
    }

    if (!!acceptedImages.length) {
      setAllImages(prevImages => [
        ...prevImages,
        ...acceptedImages.map(image => ({ file: { ...image, id: '' }, id: '' })),
      ]);

      const uploadedImages = await uploadImages(themeId, acceptedImages);

      if (uploadedImages && !!uploadedImages.length) {
        setAllImages(prevImages => [
          ...prevImages.slice(0, -acceptedImages.length),
          ...uploadedImages,
        ]);
      } else {
        setAllImages(prevImages => prevImages.slice(0, -acceptedImages.length));
      }
    }
  };

  const {
    getRootProps,
    getInputProps,
    open,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    multiple: true,
    noClick: true,
    accept: 'image/png, image/gif, image/jpg, image/jpeg, image/x-icon, image/vnd.microsoft.icon',
  });

  const getImages = () => {
    const allowedFileExtensions = ['jpg', 'jpeg', 'png', 'gif', 'ico'];

    if (!!selectedTheme?.assets) {
      const images = Object.values(selectedTheme?.assets)
        ?.filter(({ file }) =>  allowedFileExtensions.includes(file.name.split('.').pop()));

      setAllImages(images);
    }
  };

  useEffect(() => {
    getImages();
  }, [selectedTheme?.assets]);

  const showInfoText = !!(isDragAccept || isDragReject);

  const renderInfoText = () => (
    <div style={{ color: isDragAccept ? 'success.light' : 'error'}} className="text-center">
      {isDragAccept ? 'Drop images to upload' : 'Image type is not valid'}
    </div>
  );

  const showConfirmDelete = async (image) => {
    setSelectedImage(image);
    showConfirm(true);
  };
  const closeConfirmDelete = async () => {
    showConfirm(false);
  };

  const handleDelete = async (image) => {
    showConfirm(false);
    setIsOpen(false);
    setSelectedImage(null);
    setAllImages(prevImages => prevImages.filter(im => im.id !== image.id));
    await deleteThemeAsset(selectedTheme.id, image);
  };
  const handlePreview = (image) => {
    setSelectedImage(image);
    setIsOpen(true);
  };
  const onClose = () => {
    setIsOpen(false);
    setSelectedImage(null);
  };

  return (
    <div className="visual-menu-content image-gallery">
      <div className="visual-menu-main-header">
        <div onClick={onBack} className="visual-menu-icon mr-2">
          <BackIcon />
        </div>
        Image Library
      </div>

      <div className="visual-menu-main-body">
        <div className={isFocused ? 'isFocused' : isDragAccept ? 'isDragAccept' : isDragReject ? 'isDragReject' : ''} {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
          <input {...getInputProps()} />
          <div className="visual-menu-images-container">
            <div className="visual-menu-image-new" style={isDragAccept ? { borderColor: "var(--green6)" } : isDragReject ? { borderColor: "var(--red6)" } : {}} onClick={e => handleUploadImages(e)}>
              {showInfoText ? (renderInfoText()) : (
                <>
                  <ImageIcon />
                  <div>Upload</div>
                </>
              )}
            </div>
            {allImages?.map((image, idx) => (
              <ImageGalleryItem
                key={image.id || idx}
                image={image}
                onDelete={showConfirmDelete}
                onPreview={handlePreview}
              />
            ))}
          </div>
        </div>
      </div>
      <Modal open={isOpen} closeModal={onClose} className="visual-menu-image-modal">
        <div className="paylixecommerce-modal-body visual-menu-image-modal-body">
          <div className='visual-menu-image-preview'>
            <img  src={selectedImage?.file?.cdn_url} alt={selectedImage?.file?.name} />
          </div>
        </div>
        <div className="paylixecommerce-modal-footer">
          <div>
            <div className="visual-menu-image-name">{selectedImage?.file?.name}</div>
            <div className="visual-menu-image-stats">
              {moment(selectedImage?.created_at).format('ll')}
              {selectedImage?.file?.size ?
                <>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="5.25" cy="5.25" r="1.6" fill="var(--black3)" />
                </svg>
                {parseSize(selectedImage?.file?.size)}
                </>
                : null
              }
            </div>
            <div className="mt-1">
              <span className="visual-menu-image-var mr-1">{'{{'} '{selectedImage?.name}' {'}}'}</span>
              {/*<BootstrapTooltip*/}
              {/*  placement="right"*/}
              {/*  title="You can use this variable to reference this image on the theme code and on the page configuration (for SEO and metadata)."*/}
              {/*>*/}
              {/*  <span>*/}
              {/*    <QuestionIcon />*/}
              {/*  </span>*/}
              {/*</BootstrapTooltip>*/}
            </div>
          </div>
          <div style={{ alignSelf: 'flex-end' }}>
            <Button onClick={() => showConfirmDelete(selectedImage)} variant="secondary" size="large" square iconName="delete" style={{ padding: '0 0.125rem' }}></Button>
          </div>
        </div>
      </Modal>

      <Modal open={confirm} closeModal={closeConfirmDelete} confirm>
        <div className="paylixecommerce-modal-header">
          You want to delete <b>{selectedImage?.name}</b>?
        </div>
        <div className="paylixecommerce-modal-footer">
          <Button variant="warning" onClick={() => handleDelete(selectedImage)}>Delete</Button>
          <Button color="ghost" onClick={closeConfirmDelete}>Cancel</Button>
        </div>
      </Modal>

    </div>
  );
}

export default ImageGallery;
